import React from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
import { SliderDataFive } from '../data/SliderData';
import Hero from '../components/Hero';
import Gallery from '../components/Gallery';

/* ─── Fonts ──────────────────────────────────────────────────── */
const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Bebas+Neue&family=Jost:wght@300;400;500;600&display=swap');
`;

/* ─── Design Tokens ──────────────────────────────────────────── */
const gold    = '#c9a96e';
const cream   = '#f5f0e8';
const ink     = '#0e0c0a';
const warm    = '#f9f5ee';
const bodyClr = 'rgba(40, 32, 24, 0.72)';
const display = `'Cormorant Garamond', Georgia, serif`;
const sans    = `'Jost', sans-serif`;
const slab    = `'Bebas Neue', sans-serif`;

/* ─── Keyframes ──────────────────────────────────────────────── */
const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

/* ─── Motion Variants ────────────────────────────────────────── */
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

/* ─── Venue Details ──────────────────────────────────────────── */
const venueDetails = [
  { num: '6',  label: 'Guest Rooms',   sub: 'for the wedding party'   },
  { num: '6',  label: 'Bathrooms',     sub: 'private & ensuite'       },
  { num: '10', label: 'Scenic Acres',  sub: 'with a private pond'     }
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const Weddingsscreen = () => {
  return (
    <>
      <FontLoader />
      <PageWrapper>

        {/* ── Hero ──────────────────────────────────────────── */}
        <Hero slides={SliderDataFive} />

        {/* ── Masthead ──────────────────────────────────────── */}
        <Masthead>
          <MastheadNoise />
          <MastheadGlow />
          <MastheadInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            animate="show"
          >
            <EyebrowRow as={motion.div} variants={fadeUp}>
              <EyebrowLine />
              <Eyebrow>Little Heaven Bed &amp; Breakfast</Eyebrow>
              <EyebrowLine $flipped />
            </EyebrowRow>

            <MastheadTitle as={motion.div} variants={fadeUp}>
              A Southern<br /><em>Wedding</em>
            </MastheadTitle>

            <MastheadSub as={motion.div} variants={fadeUp}>
              Intimate · Natural · Unforgettable
            </MastheadSub>

            <ScrollIndicator as={motion.div} variants={fadeUp}>
              <ScrollLine />
              <ScrollText>Scroll</ScrollText>
            </ScrollIndicator>
          </MastheadInner>

          <RotatingBadge aria-hidden="true">
            <svg viewBox="0 0 100 100" width="116" height="116">
              <defs>
                <path id="wCirc" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
              </defs>
              <text fontSize="11" fill={gold} fontFamily="Jost,sans-serif" fontWeight="500" letterSpacing="3">
                <textPath href="#wCirc">WEDDINGS · ELOPEMENTS · CELEBRATIONS · </textPath>
              </text>
            </svg>
            <RotatingCenter>✦</RotatingCenter>
          </RotatingBadge>
        </Masthead>

        {/* ── Intro Statement ───────────────────────────────── */}
        <IntroSection>
          <IntroInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <IntroEyebrow as={motion.div} variants={fadeUp}>Your Dream Wedding</IntroEyebrow>

            <IntroTitle as={motion.div} variants={fadeUp}>
              Begin Your Forever<br /><em>Here</em>
            </IntroTitle>

            <IntroPara as={motion.div} variants={fadeUp}>
              It is our dream and honor to host your dream wedding at Little Heaven
              Bed &amp; Breakfast. Whether you're eloping as a couple for an intimate,
              natural, and peaceful escape, or celebrating with friends and family —
              Little Heaven provides the perfect backdrop for the most meaningful day
              of your life.
            </IntroPara>

            <IntroRule as={motion.div} variants={fadeUp} />

            <IntroQuote as={motion.div} variants={fadeUp}>
              "We invite you to bring your love, your people, and your vows
              to a Little Piece of Heaven."
            </IntroQuote>
          </IntroInner>
        </IntroSection>

        {/* ── Venue Details Stats ───────────────────────────── */}
        <StatsSection>
          <StatsNoise />
          <StatsGlow />
          <StatsInner>
            <StatsGrid
              as={motion.div}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {venueDetails.map((d, i) => (
                <StatCard key={i} as={motion.div} variants={fadeUp}>
                  <StatNum>{d.num}</StatNum>
                  <StatLine />
                  <StatLabel>{d.label}</StatLabel>
                  <StatSub>{d.sub}</StatSub>
                </StatCard>
              ))}
            </StatsGrid>

            <StatsCaption
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              Everything you need, all in one breathtaking place
            </StatsCaption>
          </StatsInner>
        </StatsSection>

        {/* ── Offerings Cards ───────────────────────────────── */}
        <OfferingsSection>
          <SectionHeader
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <SectionEyebrow as={motion.div} variants={fadeUp}>What We Offer</SectionEyebrow>
            <SectionTitle as={motion.div} variants={fadeUp}>
              Choose Your<br /><em>Celebration</em>
            </SectionTitle>
          </SectionHeader>

          <OfferingsGrid
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {[
              {
                icon: '💍',
                title: 'Elopement',
                sub: 'Just the Two of You',
                desc: 'Escape to the mountains for an intimate, private ceremony surrounded by nature. The most peaceful and personal way to begin your forever.',
              },
              {
                icon: '🌿',
                title: 'Intimate Wedding',
                sub: 'Friends & Close Family',
                desc: 'Invite your dearest to witness your vows in a breathtaking natural setting. Small enough to feel personal, beautiful enough to be unforgettable.',
              },
              {
                icon: '🥂',
                title: 'Wedding Celebration',
                sub: 'Full Party Experience',
                desc: 'Host your full wedding celebration on 10 scenic acres with a pond, 6 guest rooms, and sweeping mountain views.',
              },
            ].map((o, i) => (
              <OfferCard
                key={i}
                as={motion.div}
                variants={fadeUp}
                whileHover={{ y: -8, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
              >
                <OfferIcon>{o.icon}</OfferIcon>
                <OfferCardLine />
                <OfferTitle>{o.title}</OfferTitle>
                <OfferSub>{o.sub}</OfferSub>
                <OfferDesc>{o.desc}</OfferDesc>
              </OfferCard>
            ))}
          </OfferingsGrid>
        </OfferingsSection>

        {/* ── Dark Pull Quote Break ──────────────────────────── */}
        <DarkBreak>
          <DarkBreakNoise />
          <DarkBreakGlow />
          <DarkBreakContent
            as={motion.div}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.85 }}
          >
            <DarkBreakLabel>The Setting</DarkBreakLabel>
            <DarkBreakTitle>
              Mountain Views.<br />
              <em>Vineyard Sunsets.</em><br />
              A Private Pond.
            </DarkBreakTitle>
            <DarkBreakSub>
              Elkin, North Carolina · 10 Acres · The Mountains of Little Heaven
            </DarkBreakSub>
          </DarkBreakContent>
        </DarkBreak>

        {/* ── Gallery ───────────────────────────────────────── */}
        <Gallery />

        {/* ── Closing CTA ───────────────────────────────────── */}
        <ClosingSection>
          <ClosingInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <SectionEyebrow as={motion.div} variants={fadeUp} $light>Begin Planning</SectionEyebrow>
            <ClosingTitle as={motion.div} variants={fadeUp}>
              Ready to Say<br /><em>I Do?</em>
            </ClosingTitle>
            <ClosingPara as={motion.div} variants={fadeUp}>
              We would be honored to be part of your most special day. Reach out to us
              and let's start planning the wedding of your dreams at Little Heaven
              Bed &amp; Breakfast.
            </ClosingPara>
            <ClosingBtn
              as={motion.a}
              href="mailto:info@littleheavenbnb.com"
              variants={fadeUp}
              whileHover={{ scale: 1.03, transition: { duration: 0.25 } }}
            >
              Contact Us About Your Wedding
            </ClosingBtn>
          </ClosingInner>
        </ClosingSection>

        {/* ── Marquee Strip ─────────────────────────────────── */}
        <MarqueeStrip>
          <MarqueeInner>
            {Array(7).fill('LITTLE HEAVEN WEDDINGS · HAMPTONVILLE, NC · MOUNTAIN CEREMONIES · SOUTHERN ROMANCE ·').map((t, i) => (
              <MarqueeText key={i}>{t}</MarqueeText>
            ))}
          </MarqueeInner>
        </MarqueeStrip>

      </PageWrapper>
    </>
  );
};

export default Weddingsscreen;

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════ */

const PageWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
  background: ${warm};
  color: ${ink};
  font-family: ${sans};
`;

/* ── Masthead ─────────────────────────────────────────────────── */
const Masthead = styled.section`
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #141210 0%, #1e1a14 55%, #0e0c0a 100%);
  overflow: hidden;
  padding: 80px 24px 90px;
`;

const MastheadNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.028;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px;
  pointer-events: none;
`;

const MastheadGlow = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 65% 55% at 25% 40%, rgba(201,169,110,0.1) 0%, transparent 60%),
    radial-gradient(ellipse 45% 45% at 78% 60%, rgba(201,169,110,0.06) 0%, transparent 60%);
  pointer-events: none;
`;

const MastheadInner = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 36px;
`;

const EyebrowLine = styled.div`
  flex: 1;
  max-width: 90px;
  height: 1px;
  background: ${(p) => p.$flipped
    ? `linear-gradient(90deg, ${gold}, transparent)`
    : `linear-gradient(90deg, transparent, ${gold})`};
`;

const Eyebrow = styled.span`
  font-family: ${sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  white-space: nowrap;
`;

const MastheadTitle = styled.h1`
  font-family: ${display};
  font-size: clamp(5rem, 13vw, 12rem);
  font-weight: 300;
  line-height: 0.92;
  color: ${cream};
  margin: 0 0 32px;
  letter-spacing: -3px;

  em {
    font-style: italic;
    color: ${gold};
    display: block;
    font-size: 0.88em;
  }
`;

const MastheadSub = styled.p`
  font-family: ${sans};
  font-size: 0.85rem;
  font-weight: 300;
  letter-spacing: 5px;
  text-transform: uppercase;
  color: rgba(245,240,232,0.5);
  margin: 0 0 52px;
`;

const ScrollIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 50px;
  background: linear-gradient(180deg, transparent, ${gold});
`;

const ScrollText = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.55);
`;

const RotatingBadge = styled.div`
  position: absolute;
  bottom: 40px;
  right: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${rotateSlow} 20s linear infinite;

  @media (max-width: 768px) { display: none; }
`;

const RotatingCenter = styled.span`
  position: absolute;
  font-size: 1.3rem;
  color: ${gold};
  animation: ${rotateSlow} 20s linear infinite reverse;
`;

/* ── Intro Section ────────────────────────────────────────────── */
const IntroSection = styled.section`
  background: ${warm};
  padding: 110px 40px 90px;

  @media (max-width: 768px) { padding: 80px 24px 70px; }
`;

const IntroInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const IntroEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  &::before {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.5;
  }
`;

const IntroTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 6vw, 5.5rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${ink};
  margin: 0 0 36px;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const IntroPara = styled.p`
  font-family: ${sans};
  font-size: 1.08rem;
  font-weight: 300;
  line-height: 1.95;
  color: ${bodyClr};
  margin: 0;
`;

const IntroRule = styled.div`
  width: 64px;
  height: 1px;
  background: rgba(201,169,110,0.4);
  margin: 40px 0;
`;

const IntroQuote = styled.blockquote`
  font-family: ${display};
  font-size: clamp(1.2rem, 2.5vw, 1.75rem);
  font-style: italic;
  font-weight: 300;
  line-height: 1.6;
  color: ${ink};
  margin: 0;
  padding: 28px 32px;
  border-left: 2px solid ${gold};
  background: rgba(201,169,110,0.04);
  border-radius: 0 12px 12px 0;
`;

/* ── Stats Section ────────────────────────────────────────────── */
const StatsSection = styled.section`
  position: relative;
  background: linear-gradient(160deg, #141210 0%, #0e0c0a 100%);
  padding: 100px 40px;
  overflow: hidden;

  @media (max-width: 768px) { padding: 80px 24px; }
`;

const StatsNoise = styled(MastheadNoise)``;
const StatsGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(201,169,110,0.07) 0%, transparent 65%);
  pointer-events: none;
`;

const StatsInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  margin-bottom: 48px;

  @media (max-width: 860px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; gap: 20px 2px; }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 52px 24px 44px;
  border: 1px solid rgba(201,169,110,0.1);
  background: linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
  transition: background 0.4s, border-color 0.4s;

  &:hover {
    background: linear-gradient(135deg, rgba(201,169,110,0.05), rgba(201,169,110,0.02));
    border-color: rgba(201,169,110,0.3);
  }
`;

const StatNum = styled.div`
  font-family: ${slab};
  font-size: clamp(4rem, 7vw, 6rem);
  line-height: 1;
  color: ${gold};
  letter-spacing: 2px;
  margin-bottom: 14px;
`;

const StatLine = styled.div`
  width: 28px;
  height: 1px;
  background: rgba(201,169,110,0.35);
  margin: 0 auto 14px;
`;

const StatLabel = styled.div`
  font-family: ${sans};
  font-size: 0.95rem;
  font-weight: 500;
  color: ${cream};
  margin-bottom: 6px;
`;

const StatSub = styled.div`
  font-family: ${sans};
  font-size: 0.78rem;
  font-weight: 300;
  letter-spacing: 0.5px;
  color: rgba(245,240,232,0.4);
`;

const StatsCaption = styled.div`
  text-align: center;
  font-family: ${display};
  font-size: clamp(1.1rem, 2.2vw, 1.6rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(245,240,232,0.55);
`;

/* ── Offerings ────────────────────────────────────────────────── */
const OfferingsSection = styled.section`
  background: ${warm};
  padding: 110px 40px 90px;

  @media (max-width: 768px) { padding: 80px 24px 70px; }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${(p) => p.$light ? 'rgba(245,240,232,0.55)' : gold};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${(p) => p.$light ? 'rgba(245,240,232,0.3)' : gold};
    opacity: 0.5;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 6vw, 5.5rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${ink};
  margin: 0;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const OfferingsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 860px) { grid-template-columns: 1fr; max-width: 520px; }
`;

const OfferCard = styled.div`
  padding: 52px 40px 48px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(201,169,110,0.15);
  box-shadow: 0 4px 40px rgba(40,32,24,0.06);
  cursor: default;
  transition: border-color 0.35s, box-shadow 0.35s;

  &:hover {
    border-color: rgba(201,169,110,0.45);
    box-shadow: 0 12px 60px rgba(40,32,24,0.1);
  }
`;

const OfferIcon = styled.div`
  font-size: 2.4rem;
  margin-bottom: 20px;
  line-height: 1;
`;

const OfferCardLine = styled.div`
  width: 36px;
  height: 2px;
  background: ${gold};
  margin-bottom: 22px;
  border-radius: 2px;
  opacity: 0.5;
  transition: width 0.35s, opacity 0.35s;

  ${OfferCard}:hover & { width: 56px; opacity: 1; }
`;

const OfferTitle = styled.h3`
  font-family: ${slab};
  font-size: 2.2rem;
  letter-spacing: 2px;
  color: ${ink};
  margin: 0 0 6px;
  line-height: 1;
  transition: color 0.3s;

  ${OfferCard}:hover & { color: ${gold}; }
`;

const OfferSub = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: ${gold};
  margin-bottom: 20px;
  opacity: 0.75;
`;

const OfferDesc = styled.p`
  font-family: ${sans};
  font-size: 0.95rem;
  font-weight: 300;
  line-height: 1.85;
  color: ${bodyClr};
  margin: 0;
`;

/* ── Dark Break ───────────────────────────────────────────────── */
const DarkBreak = styled.section`
  position: relative;
  background: linear-gradient(160deg, #141210 0%, #0e0c0a 100%);
  min-height: 52vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 100px 24px;
  overflow: hidden;
`;

const DarkBreakNoise = styled(MastheadNoise)``;
const DarkBreakGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(201,169,110,0.08) 0%, transparent 65%);
  pointer-events: none;
`;

const DarkBreakContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
`;

const DarkBreakLabel = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 5px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.6);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 28px;
    height: 1px;
    background: ${gold};
    opacity: 0.35;
  }
`;

const DarkBreakTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 7vw, 7.5rem);
  font-weight: 300;
  line-height: 1.0;
  color: ${cream};
  margin: 0 0 24px;
  letter-spacing: -1.5px;

  em { font-style: italic; color: ${gold}; }
`;

const DarkBreakSub = styled.div`
  font-family: ${sans};
  font-size: 0.82rem;
  font-weight: 300;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(245,240,232,0.3);
`;

/* ── Closing Section ──────────────────────────────────────────── */
const ClosingSection = styled.section`
  background: linear-gradient(160deg, #141210 0%, #0e0c0a 100%);
  padding: 120px 40px 130px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,169,110,0.07) 0%, transparent 65%);
  }

  @media (max-width: 768px) { padding: 90px 24px 100px; }
`;

const ClosingInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const ClosingTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 7vw, 6.5rem);
  font-weight: 300;
  line-height: 1.0;
  color: ${cream};
  margin: 0 0 36px;
  letter-spacing: -1px;

  em { font-style: italic; color: ${gold}; }
`;

const ClosingPara = styled.p`
  font-family: ${sans};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.95;
  color: rgba(245,240,232,0.65);
  margin: 0 0 48px;
`;

const ClosingBtn = styled(motion.a)`
  display: inline-block;
  font-family: ${sans};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: ${ink};
  background: ${gold};
  padding: 18px 44px;
  border-radius: 100px;
  text-decoration: none;
  transition: background 0.3s;
  box-shadow: 0 8px 40px rgba(201,169,110,0.3);

  &:hover { background: #dbbf85; }
`;

/* ── Marquee ──────────────────────────────────────────────────── */
const MarqueeStrip = styled.div`
  background: ${gold};
  padding: 13px 0;
  overflow: hidden;
  white-space: nowrap;
`;

const MarqueeInner = styled.div`
  display: inline-flex;
  animation: ${marquee} 28s linear infinite;
`;

const MarqueeText = styled.span`
  font-family: ${slab};
  font-size: 0.95rem;
  letter-spacing: 3px;
  color: ${ink};
  padding: 0 24px;
`;